import React, { useState } from 'react';
import {
  Card,
  CardActionArea,
  CardContent,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Chip,
  Divider,
  IconButton,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { formatPackagePrice } from '../../utils/currency';

const formatPrice = formatPackagePrice;

const VISIBLE_DEFAULT = 3;

const PackageList = ({ packages }) => {
  const theme = useTheme();
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showAll, setShowAll] = useState(false);

  if (!packages || packages.length === 0) return null;

  const visiblePackages = showAll ? packages : packages.slice(0, VISIBLE_DEFAULT);
  const hasMore = packages.length > VISIBLE_DEFAULT;

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
          gap: 2.5,
          mt: 0.5,
        }}
      >
        {visiblePackages.map((pkg) => (
          <Card
            key={pkg.id}
            sx={{
              minWidth: 0,
              height: 220,
              borderRadius: '16px',
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
              border: `1px solid ${theme.palette.divider}`,
              transition: 'transform 0.18s ease, box-shadow 0.18s ease',
              '&:hover': {
                transform: 'translateY(-3px)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.13)',
              },
            }}
          >
            <CardActionArea
              onClick={() => setSelectedPackage(pkg)}
              sx={{ height: '100%', alignItems: 'flex-start', display: 'flex', flexDirection: 'column' }}
            >
              <CardContent sx={{ p: 3, width: '100%', minWidth: 0, flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                {/* Csomagnév */}
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: '"Playfair Display", serif',
                    fontWeight: 700,
                    color: theme.palette.primary.dark,
                    mb: 1.5,
                    lineHeight: 1.3,
                    height: '3.25rem',
                    wordBreak: 'break-word',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {pkg.name}
                </Typography>

                {/* Ár chip */}
                <Chip
                  icon={<LocalOfferIcon sx={{ fontSize: '0.9rem !important' }} />}
                  label={formatPrice(pkg.price, pkg.priceUnit)}
                  size="small"
                  sx={{
                    mb: 1.5,
                    maxWidth: '100%',
                    bgcolor: theme.palette.primary.main + '14',
                    color: theme.palette.primary.dark,
                    fontWeight: 600,
                    fontSize: '0.82rem',
                    border: `1px solid ${theme.palette.primary.main}30`,
                    '& .MuiChip-icon': { color: theme.palette.primary.main },
                    '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
                  }}
                />

                {/* Rövid leírás */}
                {pkg.shortDescription && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      wordBreak: 'break-word',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      lineHeight: 1.5,
                    }}
                  >
                    {pkg.shortDescription}
                  </Typography>
                )}
              </CardContent>

              {/* Részletek link */}
              <Box sx={{ px: 3, pb: 2, width: '100%', flexShrink: 0, textAlign: 'center' }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                    letterSpacing: 0.3,
                  }}
                >
                  Részletek megtekintése →
                </Typography>
              </Box>
            </CardActionArea>
          </Card>
        ))}
      </Box>

      {/* Több csomag gomb */}
      {hasMore && (
        <Box sx={{ textAlign: 'center', mt: 2.5 }}>
          <Button
            variant="outlined"
            onClick={() => setShowAll(prev => !prev)}
            sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, px: 3 }}
          >
            {showAll
              ? 'Kevesebb csomag mutatása'
              : `További ${packages.length - VISIBLE_DEFAULT} csomag megtekintése`}
          </Button>
        </Box>
      )}

      {/* Részletes dialóg */}
      <Dialog
        open={Boolean(selectedPackage)}
        onClose={() => setSelectedPackage(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            overflow: 'hidden',
          },
        }}
      >
        {selectedPackage && (
          <>
            <DialogTitle
              sx={{
                bgcolor: theme.palette.primary.dark,
                color: 'white',
                fontFamily: '"Playfair Display", serif',
                fontWeight: 700,
                fontSize: '1.35rem',
                pr: 6,
                py: 2.5,
              }}
            >
              {selectedPackage.name}
              <IconButton
                onClick={() => setSelectedPackage(null)}
                sx={{
                  position: 'absolute',
                  right: 12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'white',
                }}
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 3, pt: '24px !important' }}>
              {/* Ár */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 2.5,
                  p: 2,
                  bgcolor: theme.palette.primary.main + '10',
                  borderRadius: '12px',
                  border: `1px solid ${theme.palette.primary.main}25`,
                }}
              >
                <LocalOfferIcon sx={{ color: theme.palette.primary.main, fontSize: '1.2rem' }} />
                <Typography
                  variant="h6"
                  sx={{
                    color: theme.palette.primary.dark,
                    fontWeight: 700,
                  }}
                >
                  {formatPrice(selectedPackage.price, selectedPackage.priceUnit)}
                </Typography>
              </Box>

              {/* Rövid leírás */}
              {selectedPackage.shortDescription && (
                <>
                  <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7, color: 'text.secondary', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                    {selectedPackage.shortDescription}
                  </Typography>
                  {selectedPackage.fullDescription && <Divider sx={{ mb: 2 }} />}
                </>
              )}

              {/* Teljes leírás */}
              {selectedPackage.fullDescription && (
                <Box
                  className="ql-editor"
                  dangerouslySetInnerHTML={{ __html: selectedPackage.fullDescription }}
                  sx={{
                    p: '0 !important',
                    fontSize: '0.875rem',
                    lineHeight: 1.8,
                    color: 'text.primary',
                    '& ul, & ol': { pl: 2.5 },
                    '& p': { m: 0, mb: 0.5 },
                  }}
                />
              )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3 }}>
              <Button
                onClick={() => setSelectedPackage(null)}
                variant="outlined"
                sx={{ borderRadius: '10px' }}
              >
                Bezárás
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

export default PackageList;
